/**
 * Fixed fee added to each instalment charge (USD), used at terms checkboxes (not included to calculations beacuse at BE used same values and added to each instalment payment)
 */
export const INSTALMENT_FIXED_FEE_PER_PAYMENT_USD = 6;

export const INSTALMENT_PLAN_MONTHS = [4, 6, 13] as const;

export type InstalmentMonthsOption = (typeof INSTALMENT_PLAN_MONTHS)[number];

export function roundCurrency2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function parseMoneyInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n)) {
    return null;
  }
  return roundCurrency2(n);
}

export function minDownPaymentAmount(totalPrice: number): number {
  return roundCurrency2(totalPrice * 0.2);
}

export function maxDownPaymentAmount(totalPrice: number): number {
  return roundCurrency2(totalPrice * 0.5);
}

export function addCalendarMonths(start: Date, months: number): Date {
  const d = new Date(start.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

export function formatShortUsDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

export interface InstalmentSummary {
  instalmentMonths: InstalmentMonthsOption;
  certificateTotal: number;
  downPayment: number;
  remainingBalance: number;
  remainingPaymentCount: number;
  scheduledPaymentAmount: number;
  /** First instalment due date (after deposit), for inline copy. */
  nextInstalmentDate: Date;
  nextPaymentLabel: string;
}

export function buildInstalmentSummary({
  certificateTotal,
  instalmentMonths,
  downPayment,
  anchorDate = new Date(),
}: {
  certificateTotal: number;
  instalmentMonths: InstalmentMonthsOption;
  downPayment: number;
  anchorDate?: Date;
}): InstalmentSummary {
  const dp = roundCurrency2(downPayment);
  const remainingBalance = roundCurrency2(Math.max(0, certificateTotal - dp));
  const remainingPaymentCount = Math.max(0, instalmentMonths - 1);
  const scheduledPaymentAmount =
    remainingPaymentCount > 0 ? roundCurrency2(remainingBalance / remainingPaymentCount) : 0;

  const nextInstalmentDate = addCalendarMonths(anchorDate, 1);
  const nextPaymentLabel = formatShortUsDate(nextInstalmentDate);

  return {
    instalmentMonths,
    certificateTotal,
    downPayment: dp,
    remainingBalance,
    remainingPaymentCount,
    scheduledPaymentAmount,
    nextInstalmentDate,
    nextPaymentLabel,
  };
}

export interface PaymentScheduleEntry {
  rowNumber: number;
  paymentDescription: string;
  date: Date;
  amount: number;
  isDeposit: boolean;
}

export function buildInstalmentPaymentSchedule({
  certificateTotal,
  downPayment,
  instalmentMonths,
  anchorDate = new Date(),
}: {
  certificateTotal: number;
  downPayment: number;
  instalmentMonths: InstalmentMonthsOption;
  anchorDate?: Date;
}): { entries: PaymentScheduleEntry[]; total: number } {
  const anchor = new Date(anchorDate.getTime());
  const down = roundCurrency2(downPayment);
  const remaining = roundCurrency2(Math.max(0, certificateTotal - down));
  const instalmentCount = Math.max(0, instalmentMonths - 1);

  const entries: PaymentScheduleEntry[] = [
    {
      rowNumber: 1,
      paymentDescription: `Deposit 1 of ${instalmentMonths}`,
      date: new Date(anchor.getTime()),
      amount: down,
      isDeposit: true,
    },
  ];

  if (instalmentCount === 0) {
    return { entries, total: roundCurrency2(certificateTotal) };
  }

  const per = roundCurrency2(remaining / instalmentCount);
  let allocated = 0;

  for (let i = 0; i < instalmentCount; i++) {
    const isLast = i === instalmentCount - 1;
    const amount = isLast ? roundCurrency2(remaining - allocated) : per;
    allocated = roundCurrency2(allocated + amount);
    const date = addCalendarMonths(anchor, i + 1);
    entries.push({
      rowNumber: i + 2,
      paymentDescription: `Installment ${i + 2} of ${instalmentMonths}`,
      date,
      amount,
      isDeposit: false,
    });
  }

  return { entries, total: roundCurrency2(certificateTotal) };
}
